/**
 * Aspect — SH L2 light-probe field (dynamic furniture grounding)
 * --------------------------------------------------------------
 * Loads the 3D probe grid baked by tools/bake/bake_room.py (9 RGB SH L2
 * radiance coefficients per probe, day + night sets) and lights dynamically
 * placed furniture with it:
 *
 *   spatial blending — the 8 probes surrounding an object's position are
 *     trilinearly interpolated whenever the object spawns or moves;
 *   time blending    — the day and night coefficient sets are lerped by the
 *     global timeline (0 = day, 1 = night) on the CPU (27 floats per object).
 *
 * The blended coefficients are converted to irradiance (Lambert-convolved,
 * basis constants folded in) and uploaded per mesh instance as `uAmbientSH`,
 * evaluated by a custom ambientPS chunk — furniture sits in the baked GI
 * field instead of a flat scene-wide ambient color.
 */

import * as pc from "playcanvas";
import { CHUNK_API_VERSION } from "./time_blend_chunks.js";

/** Custom SH L2 ambient evaluation (uniform order matches _radianceToIrradiance). */
export const AMBIENT_SH_PS = /* glsl */ `
uniform vec3 uAmbientSH[9];
void addAmbient(vec3 worldNormal) {
    vec3 n = worldNormal;
    vec3 color =
        uAmbientSH[0] +
        uAmbientSH[1] * n.x +
        uAmbientSH[2] * n.y +
        uAmbientSH[3] * n.z +
        uAmbientSH[4] * n.x * n.z +
        uAmbientSH[5] * n.z * n.y +
        uAmbientSH[6] * n.y * n.x +
        uAmbientSH[7] * (3.0 * n.z * n.z - 1.0) +
        uAmbientSH[8] * (n.x * n.x - n.y * n.y);
    dDiffuseLight += max(color, vec3(0.0));
}
`;

/*
 * Bake order (tools/bake/bake_room.py sh_basis, constants included):
 *   L[0]=Y00, L[1]=y, L[2]=z, L[3]=x, L[4]=xy, L[5]=yz, L[6]=3z²-1, L[7]=xz, L[8]=x²-y²
 * Lambertian convolution Â_l/π: A0=1, A1=2/3, A2=1/4. Constants folded so the
 * shader only multiplies by the raw polynomial terms (engine chunk order).
 */
const C1 = (2 / 3) * 0.488603;
const C2 = 1.092548 / 4;
const C2_ZZ = 0.315392 / 4;
const C2_XY = 0.546274 / 4;

/** Raw radiance SH (bake layout) -> irradiance coefficients in shader order. */
function radianceToIrradiance(raw, out) {
  // raw: 27 floats, 9 coefficients x RGB. out: Float32Array(27), shader layout.
  for (let ch = 0; ch < 3; ch++) {
    const L = (i) => raw[i * 3 + ch];
    out[0 * 3 + ch] = 0.282095 * L(0);
    out[1 * 3 + ch] = C1 * L(3);       // * n.x
    out[2 * 3 + ch] = C1 * L(1);       // * n.y
    out[3 * 3 + ch] = C1 * L(2);       // * n.z
    out[4 * 3 + ch] = C2 * L(7);       // * n.x n.z
    out[5 * 3 + ch] = C2 * L(5);       // * n.z n.y
    out[6 * 3 + ch] = C2 * L(4);       // * n.y n.x
    out[7 * 3 + ch] = C2_ZZ * L(6);    // * (3 n.z² - 1)
    out[8 * 3 + ch] = C2_XY * L(8);    // * (n.x² - n.y²)
  }
  return out;
}

/** Clamped index of the grid cell containing v, plus the in-cell fraction. */
function gridLerp(axis, v) {
  const n = axis.length;
  if (n < 2 || v <= axis[0]) return [0, 0];
  if (v >= axis[n - 1]) return [n - 2, 1];
  for (let i = 0; i < n - 1; i++) {
    if (v < axis[i + 1]) {
      const span = axis[i + 1] - axis[i] || 1;
      return [i, (v - axis[i]) / span];
    }
  }
  return [n - 2, 1];
}

export class ProbeField {
  /**
   * @param {pc.AppBase} app
   * @param {import('../../scene_manager.js').SceneManager} scene
   */
  constructor(app, scene) {
    this.app = app;
    this.scene = scene;
    this.day = null;   // { xs, ys, zs, coeffs: Float32Array[] (irradiance layout) }
    this.night = null;
    this.active = false;
    this._timeBlend = 0;
    /** @type {Map<string, { entity: pc.Entity, sh: Float32Array }>} */
    this._tracked = new Map();
    this._materials = new Set();
    this._scratchA = new Float32Array(27);
    this._scratchB = new Float32Array(27);

    this._onAdded = (p) => this._track(p?.id);
    this._onUpdated = (p) => this._track(p?.id);
    this._onRemoved = ({ id } = {}) => this._tracked.delete(id);
  }

  async load(dayUrl, nightUrl) {
    const [day, night] = await Promise.all([
      fetch(dayUrl).then((r) => r.json()),
      fetch(nightUrl).then((r) => r.json()),
    ]);
    this.day = this._parse(day);
    this.night = this._parse(night);
  }

  _parse(json) {
    const coeffs = json.coefficients.map((raw) => radianceToIrradiance(raw, new Float32Array(27)));
    return { xs: json.xs, ys: json.ys, zs: json.zs, coeffs };
  }

  activate() {
    if (this.active || !this.day || !this.night) return;
    this.active = true;
    this.app.on("scene:placement-added", this._onAdded);
    this.app.on("scene:placement-updated", this._onUpdated);
    this.app.on("scene:placement-removed", this._onRemoved);
    for (const id of this.scene.entities.keys()) this._track(id);
  }

  deactivate() {
    if (!this.active && this._tracked.size === 0) return;
    this.app.off("scene:placement-added", this._onAdded);
    this.app.off("scene:placement-updated", this._onUpdated);
    this.app.off("scene:placement-removed", this._onRemoved);
    for (const { entity } of this._tracked.values()) {
      for (const render of entity.findComponents?.("render") ?? []) {
        for (const mi of render.meshInstances ?? []) mi.deleteParameter?.("uAmbientSH[0]");
      }
    }
    this._tracked.clear();
    for (const mat of this._materials) {
      if (mat.hasShaderChunks) mat.shaderChunks.glsl.delete("ambientPS");
      mat.update();
    }
    this._materials.clear();
    this.active = false;
  }

  setTimeBlend(t) {
    this._timeBlend = Math.max(0, Math.min(1, t));
    if (!this.active) return;
    for (const rec of this._tracked.values()) this._refresh(rec);
    this.app.renderNextFrame = true;
  }

  /** Sample blended irradiance SH at a world position (27 floats). */
  sampleAt(pos, out = new Float32Array(27)) {
    if (!this.day || !this.night) {
      out.fill(0);
      return out;
    }
    this._sample(this.day, pos, this._scratchA);
    this._sample(this.night, pos, this._scratchB);
    const t = this._timeBlend;
    for (let i = 0; i < 27; i++) {
      out[i] = this._scratchA[i] * (1 - t) + this._scratchB[i] * t;
    }
    return out;
  }

  /**
   * Track a non-placement entity (e.g. wet-room floor overlay) so its materials
   * get the ambient SH chunk and refresh with the probe field.
   */
  trackEntity(entity, key) {
    if (!this.active || !entity || !key) return;
    let rec = this._tracked.get(key);
    if (!rec) {
      rec = { entity, sh: new Float32Array(27) };
      this._tracked.set(key, rec);
      this._installChunk(entity);
    } else {
      rec.entity = entity;
    }
    this._refresh(rec);
  }

  _track(id) {
    if (!this.active || !id) return;
    const entity = this.scene.entities.get(id);
    if (!entity) return;
    let rec = this._tracked.get(id);
    if (!rec) {
      rec = { entity, sh: new Float32Array(27) };
      this._tracked.set(id, rec);
      this._installChunk(entity);
    } else {
      rec.entity = entity;
    }
    this._refresh(rec);
  }

  /** Swap the ambient chunk onto every opaque material this entity draws with. */
  _installChunk(entity) {
    for (const render of entity.findComponents?.("render") ?? []) {
      for (const mi of render.meshInstances ?? []) {
        const mat = mi.material;
        if (!mat || this._materials.has(mat)) continue;
        if (mat.blendType != null && mat.blendType !== pc.BLEND_NONE) continue;
        mat.shaderChunks.version = CHUNK_API_VERSION;
        mat.shaderChunks.glsl.set("ambientPS", AMBIENT_SH_PS);
        mat.update();
        this._materials.add(mat);
      }
    }
  }

  /** Spatial (trilinear) + time (day/night) blend, uploaded per mesh instance. */
  _refresh(rec) {
    const pos = rec.entity.getPosition();
    this._sample(this.day, pos, this._scratchA);
    this._sample(this.night, pos, this._scratchB);
    const t = this._timeBlend;
    for (let i = 0; i < 27; i++) {
      rec.sh[i] = this._scratchA[i] * (1 - t) + this._scratchB[i] * t;
    }
    for (const render of rec.entity.findComponents?.("render") ?? []) {
      for (const mi of render.meshInstances ?? []) {
        mi.setParameter("uAmbientSH[0]", rec.sh);
      }
    }
  }

  _sample(grid, pos, out) {
    const { xs, ys, zs, coeffs } = grid;
    const nx = xs.length;
    const nz = zs.length;
    const [ix, fx] = gridLerp(xs, pos.x);
    const [iy, fy] = gridLerp(ys, pos.y);
    const [iz, fz] = gridLerp(zs, pos.z);
    const at = (jx, jy, jz) => coeffs[(jy * nz + jz) * nx + jx];

    out.fill(0);
    for (let dy = 0; dy <= 1; dy++) {
      const wy = dy ? fy : 1 - fy;
      if (wy === 0) continue;
      for (let dz = 0; dz <= 1; dz++) {
        const wz = dz ? fz : 1 - fz;
        if (wz === 0) continue;
        for (let dx = 0; dx <= 1; dx++) {
          const w = wy * wz * (dx ? fx : 1 - fx);
          if (w === 0) continue;
          const c = at(ix + dx, iy + dy, iz + dz);
          for (let i = 0; i < 27; i++) out[i] += c[i] * w;
        }
      }
    }
    return out;
  }

  destroy() {
    this.deactivate();
    this.day = null;
    this.night = null;
  }
}
